/**
 * Template Library Modal
 * Main modal for browsing and selecting email templates
 *
 * Features:
 * - System templates (pre-built by Pumpd team)
 * - User templates (saved by current user/organisation)
 * - Tab-based navigation
 *
 * @file src/components/email/templates/TemplateLibraryModal.tsx
 * @created 2025-10-22
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import SystemTemplatesTab from './SystemTemplatesTab';
import UserTemplatesTab from './UserTemplatesTab';
import { Sparkles, User } from 'lucide-react';

interface TemplateLibraryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTemplateSelect: (designJson: Record<string, any>, templateId?: string) => void;
  organisationId?: string | null;
  restaurantId?: string;
}

/**
 * TemplateLibraryModal Component
 *
 * Displays a modal with two tabs:
 * 1. System Templates - Professional templates from Pumpd
 * 2. My Templates - User-created templates
 *
 * @example
 * <TemplateLibraryModal
 *   open={showModal}
 *   onClose={() => setShowModal(false)}
 *   onTemplateSelect={(designJson, templateId) => {
 *     editor.loadProjectData(designJson);
 *   }}
 *   organisationId="org-123"
 *   restaurantId="rest-456"
 * />
 */
const TemplateLibraryModal: React.FC<TemplateLibraryModalProps> = ({
  open,
  onOpenChange,
  onTemplateSelect,
  organisationId,
  restaurantId
}) => {
  const [activeTab, setActiveTab] = useState<'system' | 'user'>('system');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">
            Email Templates
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Choose a pre-designed template or select from your saved templates
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'system' | 'user')}
          className="flex-1 overflow-hidden flex flex-col"
        >
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="system" className="flex items-center gap-2 data-[state=active]:text-white group">
              <Sparkles className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
              System Templates
            </TabsTrigger>
            <TabsTrigger value="user" className="flex items-center gap-2 data-[state=active]:text-white group">
              <User className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
              My Templates
            </TabsTrigger>
          </TabsList>

          {/* System Templates Tab */}
          <TabsContent
            value="system"
            className="flex-1 overflow-y-auto mt-0 data-[state=active]:flex data-[state=active]:flex-col"
          >
            <SystemTemplatesTab
              organisationId={organisationId || ''}
              restaurantId={restaurantId}
              onTemplateSelect={(designJson, templateId) => {
                onTemplateSelect(designJson, templateId);
                onOpenChange(false);
              }}
            />
          </TabsContent>

          {/* User Templates Tab */}
          <TabsContent
            value="user"
            className="flex-1 overflow-y-auto mt-0 data-[state=active]:flex data-[state=active]:flex-col"
          >
            <UserTemplatesTab
              organisationId={organisationId || ''}
              onTemplateSelect={(designJson, templateId) => {
                onTemplateSelect(designJson, templateId);
                onOpenChange(false);
              }}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateLibraryModal;
